import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { FiSearch, FiPlus, FiEdit2, FiTrash2, FiShield, FiLock, FiUnlock, FiArrowUp, FiArrowDown } from 'react-icons/fi';
import { Modal, Button, Form } from 'react-bootstrap';

export default function Permissions() {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterModule, setFilterModule] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showModal, setShowModal] = useState(false);
  const [editingPermission, setEditingPermission] = useState(null);
  const { selectedHierarchy } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    module: '',
    category: 'read'
  });

  const modules = [
    'Dashboard',
    'Products',
    'Categories',
    'Orders',
    'Invoices',
    'Customers',
    'Employees',
    'Roles',
    'Reports',
    'Settings',
    'Stock Management'
  ];

  const categories = [
    { value: 'read', label: 'Read', color: 'info' },
    { value: 'write', label: 'Write', color: 'success' },
    { value: 'delete', label: 'Delete', color: 'danger' },
    { value: 'admin', label: 'Admin', color: 'warning' }
  ];

  useEffect(() => {
    if (selectedHierarchy?.outlet?.id) {
      loadPermissions();
    } else {
      setLoading(false);
      setPermissions([]);
    }
  }, [selectedHierarchy]);

  const loadPermissions = async () => {
    if (!selectedHierarchy?.outlet?.id) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const permissionsQuery = query(
        collection(db, 'permissions'),
        where('outletId', '==', selectedHierarchy.outlet.id)
      );
      const snapshot = await getDocs(permissionsQuery);
      const permissionsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPermissions(permissionsData);
    } catch (error) {
      console.error('Error loading permissions:', error);
      alert('Failed to load permissions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // SECURITY: Ensure outlet is selected
    if (!selectedHierarchy?.outlet?.id) {
      alert('Error: No outlet selected. Please select an outlet first.');
      return;
    }
    
    try {
      const permissionData = {
        ...formData,
        outletId: selectedHierarchy.outlet.id,
        updatedAt: new Date()
      };

      if (editingPermission) {
        // SECURITY: Verify the permission belongs to the current outlet
        if (editingPermission.outletId !== selectedHierarchy.outlet.id) {
          alert('Security Error: Cannot modify permission from another outlet.');
          return;
        }
        await updateDoc(doc(db, 'permissions', editingPermission.id), permissionData);
      } else {
        permissionData.createdAt = new Date();
        await addDoc(collection(db, 'permissions'), permissionData);
      }

      setShowModal(false);
      resetForm();
      loadPermissions();
    } catch (error) {
      console.error('Error saving permission:', error);
      alert('Failed to save permission. Please try again.');
    }
  };

  const handleEdit = (permission) => {
    // SECURITY: Ensure the permission belongs to the current outlet
    if (!selectedHierarchy?.outlet?.id) {
      alert('Error: No outlet selected. Please select an outlet first.');
      return;
    }
    
    if (permission.outletId !== selectedHierarchy.outlet.id) {
      alert('Security Error: Cannot edit permission from another outlet.');
      return;
    }
    
    setEditingPermission(permission);
    setFormData({
      name: permission.name || '',
      code: permission.code || '',
      description: permission.description || '',
      module: permission.module || '',
      category: permission.category || 'read'
    });
    setShowModal(true);
  };

  const handleDelete = async (permission) => {
    // SECURITY: Ensure the permission belongs to the current outlet
    if (!selectedHierarchy?.outlet?.id) {
      alert('Error: No outlet selected. Please select an outlet first.');
      return;
    }
    
    if (permission.outletId !== selectedHierarchy.outlet.id) {
      alert('Security Error: Cannot delete permission from another outlet.');
      return;
    }
    
    if (window.confirm(`Are you sure you want to delete "${permission.name}"?`)) {
      try {
        await deleteDoc(doc(db, 'permissions', permission.id));
        loadPermissions();
      } catch (error) {
        console.error('Error deleting permission:', error);
        alert('Failed to delete permission.');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      description: '',
      module: '',
      category: 'read'
    });
    setEditingPermission(null);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredPermissions = permissions.filter(perm => {
    // Search filter
    const matchesSearch = perm.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      perm.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      perm.module?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Module filter
    const matchesModule = !filterModule || perm.module === filterModule;
    
    // Category filter
    const matchesCategory = !filterCategory || perm.category === filterCategory;
    
    return matchesSearch && matchesModule && matchesCategory;
  }).sort((a, b) => {
    let aVal, bVal;
    
    if (sortField === 'name') {
      aVal = (a.name || '').toLowerCase();
      bVal = (b.name || '').toLowerCase();
    } else if (sortField === 'code') {
      aVal = (a.code || '').toLowerCase();
      bVal = (b.code || '').toLowerCase();
    } else if (sortField === 'module') {
      aVal = (a.module || '').toLowerCase();
      bVal = (b.module || '').toLowerCase();
    } else if (sortField === 'category') {
      aVal = (a.category || '').toLowerCase();
      bVal = (b.category || '').toLowerCase();
    }
    
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const getCategoryBadge = (category) => {
    const cat = categories.find(c => c.value === category) || categories[0];
    return { color: cat.color, label: cat.label };
  };

  const groupByModule = (perms) => {
    const grouped = {};
    perms.forEach(perm => {
      const module = perm.module || 'Other';
      if (!grouped[module]) {
        grouped[module] = [];
      }
      grouped[module].push(perm);
    });
    return grouped;
  };

  const groupedPermissions = groupByModule(filteredPermissions);

  return (
    <div className="container-fluid">
      <div className="page-header mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h1 className="page-title">Permissions</h1>
            <p className="page-subtitle">
              Manage system permissions and access controls
              {selectedHierarchy?.outlet?.name && (
                <span className="text-primary ms-2">
                  • {selectedHierarchy.outlet.name}
                </span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* No Outlet Selected Warning */}
      {!selectedHierarchy?.outlet?.id ? (
        <div className="alert alert-warning d-flex align-items-center" role="alert">
          <FiShield className="me-2" size={24} />
          <div>
            <strong>No Outlet Selected</strong>
            <p className="mb-0">Please select an outlet to manage permissions.</p>
          </div>
        </div>
      ) : (
        <>
          {/* Stats Widgets */}
          <div className="row g-4 mb-4">
        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card stat-card hover-lift">
            <div className="card-body">
              <div className="stat-card-icon" style={{ background: '#2196F3' }}>
                <FiShield className="text-white" />
              </div>
              <div className="stat-card-value">
                {permissions.length}
              </div>
              <div className="stat-card-label">Total Permissions</div>
            </div>
          </div>
        </div>

        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card stat-card hover-lift">
            <div className="card-body">
              <div className="stat-card-icon" style={{ background: '#00bcd4' }}>
                <FiUnlock className="text-white" />
              </div>
              <div className="stat-card-value">
                {permissions.filter(p => p.category === 'read').length}
              </div>
              <div className="stat-card-label">Read Permissions</div>
            </div>
          </div>
        </div>

        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card stat-card hover-lift">
            <div className="card-body">
              <div className="stat-card-icon" style={{ background: '#48bb78' }}>
                <FiUnlock className="text-white" />
              </div>
              <div className="stat-card-value">
                {permissions.filter(p => p.category === 'write').length}
              </div>
              <div className="stat-card-label">Write Permissions</div>
            </div>
          </div>
        </div>

        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card stat-card hover-lift">
            <div className="card-body">
              <div className="stat-card-icon" style={{ background: '#ffa726' }}>
                <FiLock className="text-white" />
              </div>
              <div className="stat-card-value">
                {permissions.filter(p => p.category === 'admin').length}
              </div>
              <div className="stat-card-label">Admin Permissions</div>
            </div>
          </div>
        </div>
      </div>

      {/* Permissions Table */}
      <div className="card hover-lift">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Permissions</h5>
          <button className="btn btn-sm btn-primary" onClick={() => setShowModal(true)}>
            <FiPlus className="me-2" />
            Add Permission
          </button>
        </div>

        {/* Filter Controls */}
        {!loading && permissions.length > 0 && (
          <div className="card-body border-bottom">
            <div className="row g-3 align-items-end">
              <div className="col-md-6">
                <label className="form-label small text-muted mb-1">Search</label>
                <div className="input-group">
                  <span className="input-group-text bg-light border-end-0">
                    <FiSearch size={16} className="text-muted" />
                  </span>
                  <input
                    type="text"
                    className="form-control border-start-0"
                    placeholder="Search by name, code, or module..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={() => setSearchTerm('')}
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
              <div className="col-md-3">
                <label className="form-label small text-muted mb-1">Module</label>
                <select
                  className="form-select"
                  value={filterModule}
                  onChange={(e) => setFilterModule(e.target.value)}
                >
                  <option value="">All Modules</option>
                  {modules.map(module => (
                    <option key={module} value={module}>{module}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label small text-muted mb-1">Category</label>
                <select
                  className="form-select"
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                >
                  <option value="">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>
            </div>
            {(searchTerm || filterModule || filterCategory) && (
              <div className="mt-3 d-flex justify-content-between align-items-center">
                <small className="text-muted">
                  Showing {filteredPermissions.length} of {permissions.length} permissions
                </small>
                {(searchTerm || filterModule || filterCategory) && (
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => {
                      setSearchTerm('');
                      setFilterModule('');
                      setFilterCategory('');
                    }}
                  >
                    Clear All Filters
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        <div className="card-body">
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted mt-2">Loading permissions...</p>
            </div>
          ) : filteredPermissions.length === 0 ? (
            <div className="text-center py-5">
              <FiShield size={48} className="text-muted mb-3" />
              <p className="text-muted mb-0">
                {searchTerm ? 'No permissions match your search.' : 'No permissions found.'}
              </p>
              {searchTerm && (
                <button
                  className="btn btn-sm btn-outline-primary mt-2"
                  onClick={() => setSearchTerm('')}
                >
                  Clear Search
                </button>
              )}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead>
                  <tr>
                    <th 
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('name')}
                    >
                      Permission Name
                      {sortField === 'name' && (
                        sortDirection === 'asc' ? <FiArrowUp className="ms-1" /> : <FiArrowDown className="ms-1" />
                      )}
                    </th>
                    <th 
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('code')}
                    >
                      Code
                      {sortField === 'code' && (
                        sortDirection === 'asc' ? <FiArrowUp className="ms-1" /> : <FiArrowDown className="ms-1" />
                      )}
                    </th>
                    <th 
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('module')}
                    >
                      Module
                      {sortField === 'module' && (
                        sortDirection === 'asc' ? <FiArrowUp className="ms-1" /> : <FiArrowDown className="ms-1" />
                      )}
                    </th>
                    <th 
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('category')}
                    >
                      Category
                      {sortField === 'category' && (
                        sortDirection === 'asc' ? <FiArrowUp className="ms-1" /> : <FiArrowDown className="ms-1" />
                      )}
                    </th>
                    <th>Description</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPermissions.map(permission => {
                    const badge = getCategoryBadge(permission.category);
                    return (
                      <tr key={permission.id}>
                        <td>
                          <div className="d-flex align-items-center">
                            <div 
                              className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-2"
                              style={{
                                width: '40px',
                                height: '40px',
                                minWidth: '40px',
                                minHeight: '40px',
                                fontSize: '16px',
                                fontWeight: '600'
                              }}
                            >
                              <FiShield size={20} />
                            </div>
                            <span className="fw-semibold">{permission.name}</span>
                          </div>
                        </td>
                        <td>
                          <code className="text-primary">{permission.code}</code>
                        </td>
                        <td>
                          <span className="badge bg-secondary bg-opacity-10 text-secondary">
                            {permission.module || 'N/A'}
                          </span>
                        </td>
                        <td>
                          <span className={`badge bg-${badge.color}`}>
                            {badge.label}
                          </span>
                        </td>
                        <td>
                          <span className="text-muted">{permission.description || '-'}</span>
                        </td>
                        <td>
                          <div className="d-flex gap-2 justify-content-end">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleEdit(permission)}
                            >
                              <FiEdit2 size={14} />
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDelete(permission)}
                            >
                              <FiTrash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={() => { setShowModal(false); resetForm(); }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingPermission ? 'Edit Permission' : 'Add New Permission'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <div className="row g-3">
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Permission Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., View Products"
                    required
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Permission Code *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                    placeholder="e.g., products.view"
                    required
                  />
                  <Form.Text className="text-muted">
                    Use lowercase with dots or underscores
                  </Form.Text>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Module *</Form.Label>
                  <Form.Select
                    value={formData.module}
                    onChange={(e) => setFormData({ ...formData, module: e.target.value })}
                    required
                  >
                    <option value="">Select Module</option>
                    {modules.map(module => (
                      <option key={module} value={module}>{module}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Category *</Form.Label>
                  <Form.Select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    required
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-12">
                <Form.Group>
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe what this permission allows users to do..."
                  />
                </Form.Group>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => { setShowModal(false); resetForm(); }}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editingPermission ? 'Update' : 'Add'} Permission
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
      </>
      )}
    </div>
  );
}
